import { TimeoutAction, TimeoutActionEnum } from "helpers/context/Authentication/AuthenticationContext";
import API from 'components/Login/LoginAPI';
import { AuthorizationTypeEnum } from "helpers/enums";
import LogoutAPI from "components/Login/LogoutAPI";
import { ResponseTypeEnum } from "helpers/WebResponse";
import Constants from "helpers/constants";
import { isNullOrWhiteSpace } from "helpers/objects";
import { IBearerToken } from "helpers/interfaces";
import { Buffer } from "buffer";

export default class AuthHandler {

    #serverTimeoutMS: number = 30 * 60 * 1000; //default to 30 minutes
    #clientTimeoutMS: number = 29 * 60 * 1000; //default to 29 minutes
    #dispatch: React.Dispatch<TimeoutAction>;
    #currentTimer: NodeJS.Timeout | null = null;
    #currentServerEndTime: number | undefined;

    constructor(dispatch: React.Dispatch<TimeoutAction>) {
        this.#dispatch = dispatch;
    }

    private ResetState = () => {
        this.#dispatch({ action: TimeoutActionEnum.Reset });
    }

    private SetSignedIn = () => {
        this.#dispatch({ action: TimeoutActionEnum.SetSignin, value: true });
    }

    private SetSignedInWithOnlineAckToken = () => {
        this.#dispatch({ action: TimeoutActionEnum.SetSigninWithOnlineAckToken, value: true });
    }    

    private SetSignedInWithBearerToken = () => {
        this.#dispatch({ action: TimeoutActionEnum.SetSignInWithBearerToken, value: true });
    }  

    private ClearTimeout = () => {
        if (this.#currentTimer) {
            clearTimeout(this.#currentTimer);
            this.#currentTimer = null;
        }
    }

    public GetServerTimeRemaining = () => {
        if (this.#currentServerEndTime) {
            return this.#currentServerEndTime - Date.now();
        }
        return 0;
    }

    public ChangeTimeout = (minutes: number) => {

        const timerExists: boolean = !!this.#currentTimer;

        this.ClearTimeout();
        this.#serverTimeoutMS = Math.floor(minutes * 60 * 1000);
        this.#clientTimeoutMS = Math.floor(minutes * 60 * 1000) - (10 * 1000); //reduce the configured timeout by 10 seconds to have the client timeout before the site does.

        if (timerExists)
            this.StartTimer();
    }

    public StartTimer = () => {

        this.ClearTimeout();

        this.#currentServerEndTime = Date.now() + this.#serverTimeoutMS;
        this.#currentTimer = setTimeout(() => {
            this.#dispatch({ action: TimeoutActionEnum.TimedOut })
            this.ClearTimeout();
        }, this.#clientTimeoutMS)
    }

    public ResetTimer = () => {

        // The server side cookie will only reset it's expiration if its more than halfway through the expiration window
        // https://docs.microsoft.com/en-us/dotnet/api/microsoft.aspnetcore.authentication.cookies.cookieauthenticationoptions.slidingexpiration?view=aspnetcore-6.0
        if (this.GetServerTimeRemaining() < (this.#serverTimeoutMS / 2)) {
            this.StartTimer();
        }
    }

    public SignInAsync: (userName: string, password: string, loginType: AuthorizationTypeEnum, configuratorKey?: string) => Promise<{success: boolean, type: ResponseTypeEnum}> = async (userName, password, loginType, configuratorKey) => {

        const t = await API.SignInAsync(userName, password, loginType, configuratorKey);

        if (t.BadRequest || t.AuthRequired) {
            this.ClearTimeout();
            return {success: false, type: t.GetResponseType()};
        } else {
            this.SetUserAsAuthenticated();
            return {success: true, type: t.GetResponseType()};
        }
    }

    public SignInTokenAsync: (userName: string, password: string, loginType: AuthorizationTypeEnum, configuratorKey?: string) => Promise<{token: IBearerToken, type: ResponseTypeEnum}> = async (userName, password, loginType, configuratorKey) => {

        const t = await API.TokenAsync(userName, password, loginType, configuratorKey);

        if (t.BadRequest || t.AuthRequired) {
            localStorage.removeItem(Constants.LocalStorageNames.LOCAL_STORAGE_TOKEN_KEY);
            return {token: {token: "", clientID: "", refreshToken: ""}, type: t.GetResponseType()};
        } else {
            localStorage.setItem(Constants.LocalStorageNames.LOCAL_STORAGE_TOKEN_KEY, JSON.stringify(t.Result));
            this.SetUserAsBearerAuthenticated();
            return {token: t.Result, type: t.GetResponseType()};
        }
    }

    public SignInWithTokenAsync: (token: string) => Promise<void> = async (token) => {
        try {
            const tokenDecode = JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
            localStorage.setItem(Constants.LocalStorageNames.LOCAL_STORAGE_TOKEN_KEY, JSON.stringify({token: token, clientID: tokenDecode.ClientID}));
            const t = await API.GetRefreshTokenAsync();
            localStorage.setItem(Constants.LocalStorageNames.LOCAL_STORAGE_TOKEN_KEY, JSON.stringify({token: token, clientID: tokenDecode.ClientID, refreshToken: t.Result}));
            this.SetUserAsBearerAuthenticated();
        } catch {
            localStorage.removeItem(Constants.LocalStorageNames.LOCAL_STORAGE_TOKEN_KEY);
        }
    }

    public SignInWithRemoteIDAsync: (remoteID: string, userName: string) => Promise<boolean> = async (remoteID, userName) => {
        const t = await API.SignInWithRemoteIDAsync(userName, remoteID);

        if (t.BadRequest || t.AuthRequired) {
            this.ClearTimeout();
            return false;
        } else {
            this.SetUserAsAuthenticated();
            return true;
        }
    }

    public SignInWithOnlineAckTokenAsync: (token: string) => Promise<boolean> = async (token) => {
        const t = await API.SignInWithOnlineAckTokenAsync(token);

        if (t.BadRequest || t.AuthRequired) {
            this.ClearTimeout();
            return false;
        } else {
            this.SetSignedInWithOnlineAckToken();
            this.StartTimer();
            return true;
        }        
    }

    public SignOutAsync: () => Promise<void> = async () => {

        //Reset the state before calling logout, otherwise the request handlers will handle an unauthorized resonse and call into this again (infinitely)
        this.ClearTimeout();
        this.ResetState();

        const localTokenString = localStorage.getItem(Constants.LocalStorageNames.LOCAL_STORAGE_TOKEN_KEY);
        let response = await LogoutAPI.LogoutAsync();
        if (response.GetResponseType() === ResponseTypeEnum.Ok) {
            if (isNullOrWhiteSpace(localTokenString) || localTokenString === "undefined" || localTokenString === "null") {
                const redirectUrl = response.Result;
                if (redirectUrl) {
                    window.location.assign(redirectUrl);
                }
            } else {
                localStorage.removeItem(Constants.LocalStorageNames.LOCAL_STORAGE_TOKEN_KEY);
            }
        }
    }

    public SetUserAsAuthenticated() {
        this.SetSignedIn();
        this.StartTimer();
    }

    public SetUserAsBearerAuthenticated() {
        this.SetSignedInWithBearerToken();
    }

};


